import { Fragment, useMemo } from "react";
import { useQuery, type QueryStatus } from "@tanstack/react-query";
import { format, subDays, subMonths } from "date-fns";
import { AlertTriangle, CheckCircle2, Zap } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { Card, CardAction, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedContent } from "@/components/layout/locked-content";

import { cn } from "@/lib/utils";
import { PaidMediaApiFactory } from "@/apis/brand-os/paid-media";
import type { AdsInsight } from "@/schema/brand-os/paid-media";

type InsightStyle = { Icon: LucideIcon; iconColor: string };

const severityStyles: Record<string, InsightStyle> = {
  critical: { Icon: AlertTriangle, iconColor: "text-destructive" },
  warning: { Icon: Zap, iconColor: "text-amber-500" },
  positive: { Icon: CheckCircle2, iconColor: "text-success" },
  info: { Icon: CheckCircle2, iconColor: "text-success" },
};

const defaultStyle: InsightStyle = { Icon: Zap, iconColor: "text-amber-500" };

function InsightCard({ insight }: { insight: AdsInsight }) {
  const { Icon, iconColor } = severityStyles[insight.severity] ?? defaultStyle;
  return (
    <div className="rounded-xl border p-5 space-y-1.5">
      <div className="flex items-start gap-3">
        <Icon className={cn("size-5 mt-0.5 shrink-0", iconColor)} strokeWidth={2.5} />
        <div className="flex-1 space-y-1">
          <p className="text-sm font-semibold text-foreground">{insight.title}</p>
          <p className="text-sm text-muted-foreground leading-relaxed">{insight.description}</p>
          <p className="text-xs text-muted-foreground/70 pt-0.5">Source: {insight.source}</p>
        </div>
      </div>
    </div>
  );
}

function KeyInsightsList({ insights }: { insights: AdsInsight[] }) {
  if (!insights.length) return <LockedContent message="Insights will appear once enough data is available" />;
  return (
    <Fragment>
      {insights.map((insight, i) => (
        <InsightCard key={i} insight={insight} />
      ))}
    </Fragment>
  );
}

function KeyInsightsContent({ status, insights }: { status: QueryStatus; insights: AdsInsight[] }) {
  switch (status) {
    case "pending":
      return <KeyInsightsSkeleton />;
    case "error":
      return <LockedContent message="Insights will appear once enough data is available" />;
    case "success":
      return <KeyInsightsList insights={insights} />;
  }
}

export function KeyInsights() {
  const params = useMemo(() => {
    const yesterday = subDays(new Date(), 1);
    return {
      date_range_start: format(subMonths(yesterday, 1), "yyyy-MM-dd"),
      date_range_end: format(yesterday, "yyyy-MM-dd"),
    };
  }, []);

  const { data, status } = useQuery(PaidMediaApiFactory.Queries.FetchInsights(params));
  const insights = data?.insights ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base font-semibold">Key Insights</CardTitle>
        <CardAction>{status === "pending" ? <Skeleton className="h-4 w-32" /> : <p className="text-xs text-muted-foreground">Across all recent reports</p>}</CardAction>
      </CardHeader>
      <CardContent className="max-h-[540px] overflow-y-auto space-y-3 scrollbar-hidden">
        <KeyInsightsContent status={status} insights={insights} />
      </CardContent>
    </Card>
  );
}

function KeyInsightsSkeleton() {
  return (
    <Fragment>
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="rounded-xl border p-5 space-y-2">
          <div className="flex items-center gap-3">
            <Skeleton className="size-6 rounded-full" />
            <Skeleton className="h-5 w-44" />
          </div>
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-36 mt-1" />
        </div>
      ))}
    </Fragment>
  );
}
